import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faCalendar, faClock, faEdit, faExclamationTriangle, faFilter, faInfoCircle, faSearch, faTrash, faUser } from '@fortawesome/free-solid-svg-icons';
import { useAbsenceStore } from '../store/useAbsenceStore';
import { useToast } from '@/hooks/useToast';
import { AbsenceType } from '@/api/generated';
import dayjs from 'dayjs';

interface AbsenceListProps {
  salonId?: string;
  onEditAbsence?: (absence: any) => void;
  onDeleteAbsence?: (absence: any) => void;
}

const AbsenceList: React.FC<AbsenceListProps> = ({ salonId, onEditAbsence }) => {
  const toast = useToast();
  const { absences, loading, error, loadAbsences, deleteAbsence, clearError } = useAbsenceStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    loadAbsences();
  }, [loadAbsences, salonId]);

  useEffect(() => {
    if (error) {
      toast.error('Chyba', error);
      clearError();
    }
  }, [error, toast, clearError]);

  const filteredAbsences = absences.filter(absence => {
    const matchesSearch =
      absence.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      absence.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      absence.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || absence.type === filterType;
    return matchesSearch && matchesType;
  });

  const getTypeColor = (type: AbsenceType) => {
    switch (type) {
      case AbsenceType.Vacation:
        return 'bg-blue-100 text-blue-800';
      case AbsenceType.Illness:
        return 'bg-red-100 text-red-800';
      case AbsenceType.Private:
        return 'bg-yellow-100 text-yellow-800';
      case AbsenceType.Block:
        return 'bg-orange-100 text-orange-800';
      case AbsenceType.CalendarBlock:
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeText = (type: AbsenceType) => {
    switch (type) {
      case AbsenceType.Vacation:
        return 'Dovolená';
      case AbsenceType.Illness:
        return 'Nemoc';
      case AbsenceType.Private:
        return 'Osobní';
      case AbsenceType.Block:
        return 'Blokování';
      case AbsenceType.CalendarBlock:
        return 'Kalendářní blokování';
      default:
        return 'Neznámý';
    }
  };

  const isActive = (startDate: string, endDate: string) => {
    const today = dayjs().format('YYYY-MM-DD');
    return today >= startDate && today <= endDate;
  };

  const isUpcoming = (startDate: string) => {
    const today = dayjs().format('YYYY-MM-DD');
    return startDate > today;
  };

  const handleDeleteAbsence = async (absence: any) => {
    if (window.confirm(`Opravdu chcete smazat absenci "${absence.name}"?`)) {
      try {
        await deleteAbsence(absence.id);
        toast.success('Úspěch', 'Absence byla úspěšně smazána.');
      } catch (error) {
        // Error is handled by store
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        <span className="ml-2 text-gray-600">Načítání absencí...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-50 rounded-lg">
              <FontAwesomeIcon icon={faBan} className="text-xl text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Celkem absencí</p>
              <p className="text-2xl font-bold text-gray-900">{absences.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-50 rounded-lg">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-xl text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Aktivní</p>
              <p className="text-2xl font-bold text-gray-900">{absences.filter(a => isActive(a.startDate, a.endDate)).length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-50 rounded-lg">
              <FontAwesomeIcon icon={faCalendar} className="text-xl text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Nadcházející</p>
              <p className="text-2xl font-bold text-gray-900">{absences.filter(a => isUpcoming(a.startDate)).length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-50 rounded-lg">
              <FontAwesomeIcon icon={faUser} className="text-xl text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Typy absencí</p>
              <p className="text-2xl font-bold text-gray-900">{[...new Set(absences.map(a => a.type))].length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Hledat absence..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
            />
          </div>
          <select
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
          >
            <option value="all">Všechny typy</option>
            <option value={AbsenceType.Vacation}>Dovolená</option>
            <option value={AbsenceType.Illness}>Nemoc</option>
            <option value={AbsenceType.Private}>Osobní</option>
            <option value={AbsenceType.Block}>Blokování</option>
            <option value={AbsenceType.CalendarBlock}>Kalendářní blokování</option>
          </select>
          <button className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            <FontAwesomeIcon icon={faFilter} className="w-4 h-4 mr-2" />
            Více filtrů
          </button>
        </div>
      </div>

      {/* Absences List */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Název</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Typ</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zaměstnanec</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datum a čas</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stav</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Akce</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAbsences.map(absence => (
                <tr key={absence.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{absence.name}</div>
                      {absence.description && <div className="text-sm text-gray-500">{absence.description}</div>}
                      <div className="text-xs text-gray-400 mt-1">ID: {absence.id}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(absence.type)}`}>{getTypeText(absence.type)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{absence.userName || 'Všichni zaměstnanci'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {dayjs(absence.startDate).format('DD.MM.YYYY')}
                      {absence.startDate !== absence.endDate && <span> - {dayjs(absence.endDate).format('DD.MM.YYYY')}</span>}
                    </div>
                    <div className="text-sm text-gray-500 flex items-center">
                      <FontAwesomeIcon icon={faClock} className="w-3 h-3 mr-1" />
                      {absence.startTime} - {absence.endTime}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isActive(absence.startDate, absence.endDate) ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="w-3 h-3 mr-1" />
                        Aktivní
                      </span>
                    ) : isUpcoming(absence.startDate) ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <FontAwesomeIcon icon={faCalendar} className="w-3 h-3 mr-1" />
                        Nadcházející
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        <FontAwesomeIcon icon={faClock} className="w-3 h-3 mr-1" />
                        Dokončeno
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button className="text-blue-600 hover:text-blue-900 p-1" title="Zobrazit detail">
                        <FontAwesomeIcon icon={faInfoCircle} className="w-4 h-4" />
                      </button>
                      <button onClick={() => onEditAbsence?.(absence)} className="text-gray-600 hover:text-gray-900 p-1" title="Upravit">
                        <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDeleteAbsence(absence)} className="text-red-600 hover:text-red-900 p-1" title="Smazat">
                        <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredAbsences.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <FontAwesomeIcon icon={faBan} className="text-4xl text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Žádné absence</h3>
          <p className="text-gray-600">
            {searchTerm || filterType !== 'all' ? 'Nebyly nalezeny žádné absence odpovídající filtru.' : 'Zatím nejsou vytvořené žádné absence.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default AbsenceList;
